import React from 'react';
import { Link as ScrollLink } from 'react-scroll';
import VideoPlayer from '../../../components/common/VideoPlayer';
import AppMarquee from '../components/AppMarquee';
import DownloadButton from '../../../components/common/DownloadButton';
import SocialProof from '../../../components/common/SocialProof';
import HeroTestimonials from '../components/HeroTestimonials';
import { ShieldCheck, Github, Zap } from 'lucide-react';
import { sectionHeading, sectionSubtitle } from '../../../styles/typography';

const FeatureBadge: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 0,
      gap: 'clamp(3px, 1.2vw, 6px)',
      padding: '7px clamp(8px, 2.6vw, 15px)',
      borderRadius: '999px',
      background: 'var(--vi-bg-surface-tertiary)',
      border: '1px solid var(--vi-border-strong)',
      boxShadow: '0 2px 8px var(--vi-shadow-sm)',
      fontFamily: 'Nunito, sans-serif',
      fontSize: 'clamp(9.5px, 2.7vw, 14px)',
      fontWeight: 600,
      letterSpacing: '0.01em',
      color: 'var(--vi-text-primary)',
      whiteSpace: 'nowrap',
    }}
  >
    {icon}
    {text}
  </div>
);

const HeroSection = () => {
  return (
    <section style={{ background: 'var(--vi-bg-page)' }} className="flex flex-col items-center justify-center pt-10 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 w-full">
        <div className="text-center max-w-6xl mx-auto">

          {/* Caveat kicker */}
          <p
            style={{
              fontFamily: 'Caveat, cursive',
              fontSize: '20px',
              fontWeight: 600,
              color: 'var(--vi-accent-primary)',
              marginBottom: '12px',
              letterSpacing: '0.01em',
            }}
          >
            dictation for macOS
          </p>

          {/* Lora headline */}
          <h1
            style={{
              fontFamily: 'Lora, Georgia, serif',
              fontSize: 'clamp(38px, 10.5vw, 132px)',
              fontWeight: 700,
              color: 'var(--vi-text-primary)',
              letterSpacing: '-0.03em',
              lineHeight: 1,
              marginBottom: '24px',
            }}
          >
            Write at the
            <br />
            <span
              style={{
                color: 'var(--vi-accent-primary)',
                display: 'inline-block',
                fontStyle: 'italic',
                whiteSpace: 'nowrap',
              }}
            >
              speed of thought
            </span>
          </h1>

          {/* Nunito subheadline */}
          <p
            style={{
              fontFamily: 'Nunito, sans-serif',
              fontSize: '18px',
              color: 'var(--vi-text-secondary)',
              maxWidth: '560px',
              margin: '0 auto 36px',
              lineHeight: 1.7,
            }}
          >
            VoiceInk uses{' '}
            <span style={{ color: 'var(--vi-text-primary)', fontWeight: 600 }}>
              local AI models
            </span>{' '}
            to instantly transcribe what you say to text with near-perfect accuracy and{' '}
            <span style={{ color: 'var(--vi-text-primary)', fontWeight: 600 }}>
              complete privacy
            </span>
            .
          </p>

          {/* Feature Badges */}
          <div
            className="flex items-center justify-center mb-8"
            style={{
              flexWrap: 'nowrap',
              gap: 'clamp(5px, 2.2vw, 12px)',
              maxWidth: '100%',
              width: '100%',
            }}
          >
            <FeatureBadge
              icon={<ShieldCheck style={{ width: 'clamp(12px, 3.2vw, 16px)', height: 'clamp(12px, 3.2vw, 16px)', color: 'var(--vi-icon-support)', flexShrink: 0 }} />}
              text="100% Private"
            />
            <FeatureBadge
              icon={<Github style={{ width: 'clamp(12px, 3.2vw, 16px)', height: 'clamp(12px, 3.2vw, 16px)', color: 'var(--vi-icon-secondary)', flexShrink: 0 }} />}
              text="Open Source"
            />
            <FeatureBadge
              icon={<Zap style={{ width: 'clamp(12px, 3.2vw, 16px)', height: 'clamp(12px, 3.2vw, 16px)', color: 'var(--vi-icon-highlight)', flexShrink: 0 }} />}
              text="Lightning Fast"
            />
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center justify-center gap-4 mb-8">
            <ScrollLink
              to="pricing"
              smooth={true}
              duration={500}
              offset={-64}
              className="w-auto cursor-pointer"
            >
              <DownloadButton style={{ minWidth: '222px', paddingTop: '12px', paddingBottom: '12px' }}>
                Download Now
              </DownloadButton>
            </ScrollLink>

            <a
              href="/ios"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontFamily: 'Nunito, sans-serif',
                fontSize: '14px',
                color: 'var(--vi-text-body)',
                textDecoration: 'none',
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--vi-accent-primary)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--vi-text-body)')}
            >
              <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <span style={{ fontWeight: 500 }}>Now available on iOS</span>
              <svg style={{ width: '12px', height: '12px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>

          {/* Social Proof */}
          <SocialProof userCount={5696} className="mb-16" />
        </div>

        {/* Hero Testimonials */}
        <div className="w-full mt-12">
          <HeroTestimonials />
        </div>

        {/* Video Section */}
        <div className="relative mx-auto overflow-visible w-full max-w-5xl mt-16">
          <div className="aspect-video w-full">
            <VideoPlayer
              src="/videos/FastVoiceInk.mp4"
              containerClassName="rounded-2xl bg-[var(--vi-bg-card)] shadow-md border border-[var(--vi-border-primary)] w-full h-full"
              className="transition-all duration-300"
            />
          </div>
        </div>
      </div>

      {/* Works Across All Apps */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="text-center mb-10">
          <h2 style={{ ...sectionHeading, marginBottom: '16px' }}>
            Works Across All Apps
          </h2>
          <p style={{ ...sectionSubtitle, maxWidth: '28rem', margin: '0 auto' }}>
            Use your voice to write anywhere, in any application
          </p>
        </div>
      </div>

      {/* App Marquee */}
      <div className="w-full">
        <AppMarquee />
      </div>
    </section>
  );
};

export default HeroSection;
